import * as React from "react";
import { cn } from "../../lib/cn";

export interface SurfaceCommandBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Primary in-hub view switcher (the ``SubTabBar`` / segmented control:
   * Overview · Pipeline · Contacts …). This is NAVIGATION, not a filter — it
   * swaps the whole surface — so it is the leading, always-visible element and
   * MUST NOT be hidden inside the ``Filtri`` popover. Scrolls horizontally on
   * a narrow pane instead of wrapping.
   */
  views?: React.ReactNode;
  /**
   * Inline count / summary shown at the far left — e.g. "13 non lette".
   * ``tabular-nums`` is applied; wrap the figure in ``<b>``/``<strong>`` to
   * promote it to ``ink-1 font-semibold``.
   */
  count?: React.ReactNode;
  /** Search field (e.g. ``WorkspaceSearchBar``) — flexes to fill the row. */
  search?: React.ReactNode;
  /** ``Filtri ▾`` popover trigger — houses the dataset facets. */
  filters?: React.ReactNode;
  /**
   * Optional status note pinned to the LEFT edge of the controls cluster —
   * e.g. "Nessun evento" when the selected period is empty. Present only when
   * there is something to say: passing it makes the cluster grow so the note
   * sits at the far left of the button row, while the buttons stay right-aligned.
   */
  note?: React.ReactNode;
  /**
   * Optional range / sort segmented control (e.g. calendar Giorno·Settimana·
   * Mese, finance 4w–52w). Sits in the right cluster between ``filters`` and
   * ``actions`` so it is placed identically in every hub that has one.
   */
  range?: React.ReactNode;
  /**
   * Right-aligned actions cluster. Convention: secondary controls (account
   * picker, grouping toggle, refresh) as ghost/icon buttons FIRST, then exactly
   * ONE filled-orange primary ("+ Nuovo") as the last element.
   */
  actions?: React.ReactNode;
}

/**
 * SurfaceCommandBar — the single-row hub chrome (Jul 2026).
 *
 * Collapses the stacked ``SurfaceHeader`` + ``CompanionHero`` + search + filter
 * rows into ONE line:
 *
 *   [count] · [search — flex] · [quick filter] · [Filtri ▾] · [actions]
 *
 * The workspace tab already names the area, so no big decorative title band is
 * needed above the list — the count carries the only summary. Every hub can
 * adopt it by passing it as the ``SurfaceLayout`` ``header`` slot and dropping
 * its ``hero`` + ``toolbar`` (DRY: one row, identical across surfaces).
 *
 * Pencil aesthetic (per @ando/ui review skill): typography-first, NO card fill
 * — just a ``border-hairline`` underline on the translucent cream pane, tight
 * padding, one orange accent (owned by the children, not the bar). Wraps
 * gracefully: on a narrow pane the search drops to its own line below so the
 * body never scrolls sideways.
 */
export const SurfaceCommandBar = React.forwardRef<HTMLDivElement, SurfaceCommandBarProps>(
  ({ views, count, search, filters, note, range, actions, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-wrap items-center gap-x-2 gap-y-1.5 border-hairline border-b px-4 py-2",
        className,
      )}
      {...rest}
    >
      {views ? (
        // Leading, always-visible view switcher. ``-my-1`` lets the pill chips
        // reach full height inside the tight row; ``overflow-x-auto`` keeps a
        // long tab set on one line instead of wrapping the whole bar.
        <div className="-my-1 flex min-w-0 shrink items-center overflow-x-auto">{views}</div>
      ) : null}
      {count ? (
        <span className="shrink-0 whitespace-nowrap text-[12px] text-ink-2 tabular-nums [&_b]:font-semibold [&_b]:text-ink-1 [&_strong]:font-semibold [&_strong]:text-ink-1">
          {count}
        </span>
      ) : null}
      {search ? (
        // Full-width below on narrow panes (order-last) → inline flex-1 from sm.
        <div className="order-last w-full min-w-[160px] sm:order-none sm:w-auto sm:flex-1">
          {search}
        </div>
      ) : null}
      {filters || range || actions || note ? (
        // Right cluster — always the same order: [note] · Filtri ▾ · Range ·
        // Actions. ``ml-auto`` anchors it right even when there is no flex
        // search. With a ``note`` the cluster also grows to fill its row, so
        // the note lands at the far left of the button row (``mr-auto``) while
        // ``justify-end`` keeps the buttons where they always are.
        <div
          className={cn(
            "ml-auto flex shrink-0 items-center gap-1.5",
            note ? "grow justify-end" : undefined,
          )}
        >
          {note ? (
            <span className="mr-auto min-w-0 truncate text-[12px] text-ink-3">{note}</span>
          ) : null}
          {filters}
          {range}
          {actions}
        </div>
      ) : null}
    </div>
  ),
);
SurfaceCommandBar.displayName = "SurfaceCommandBar";
